/**
 * One activation's runs — /w/:slug/activations/:sourceId/:syncId (flag-gated).
 *
 * The route carries BOTH ids on purpose. A sync is only reachable through its source
 * (`listSyncsForSource`); there is no `getSync(syncId)` on the platform, so a `:syncId`-only route
 * would have to fan out across every audience in the workspace just to find one row.
 *
 * "Latest run" is not an endpoint — it is `listSyncRuns` with `limit: 1` over the server's default
 * run window (`RUN_WINDOW_DAYS`, 30 days). That is a query default, not this connection's actual
 * retention — retention is per-connection config, which `retentionDays()` (`lib/run-view.ts`) reads
 * for `RunsPanel` below. That `limit: 1` call lives in `useRunSync`, which owns both the poll and the
 * four-state button machine, because the two are mutually dependent: the poll cadence tightens while
 * a transition is in flight, and only a settled poll can end one. It is a SEPARATE query from
 * `RunsPanel`'s own paged read of the same endpoint — folding the two together was tried and reverted
 * (see the note on `useRunSync` in `hooks/useRunSync.ts`): the button's cadence has to tighten during
 * a transition in a way the table's poll never does, and folding onto `usePastRuns`'s own poll would
 * lose that tightened cadence outright — that hook's poll never tightens below 10s.
 *
 * There used to be two TABS here — Overview (this `limit: 1` read) and Past runs (the paged read) —
 * so the latest run and the run history could disagree and the run list vanished whenever you looked
 * at a run. One panel now holds both: `RunsPanel` renders the table and the open run's detail side by
 * side. The disabled/enabled fork below no longer REPLACES that panel — a disabled sync's run history
 * is exactly what someone reaches for when a sync is paused (why was it paused, what did it last do,
 * did it fail), so `!enabled` renders a NOTICE above the panel instead; the panel itself renders
 * unconditionally once `detail.sync` has arrived. This regressed once already: the first cut of the
 * tab deletion collapsed the disabled case into a full-panel empty state, which took the run table
 * away along with the tabs it was removing.
 *
 * The header is TWO BANDS: a breadcrumb, and the `ActivationStrip` — wrapped in the page's `<h1>` — as
 * the title, with the run control on the right. It is titled by the whole relation rather than by half
 * of it, which is what the strip has said it is for since it existed; the far side alone was the old
 * heading, and it was restated by the strip one line below. Everything that used to sit under it is
 * gone: the two trailing breadcrumbs (the strip, verbatim), the destination chip, the copyable sync id
 * (it is in the URL), and the cadence.
 *
 * The far side still climbs the same three-rung ladder the list uses — name, far-side id, account — and
 * the page still repairs that name silently, once, when it opens on a nameless sync. A name is never
 * blanked, and no failure of it takes over the screen; the ids the route carries are always enough to
 * render everything else.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { REPAIR_AUDIENCE_FIRST } from '@/_shared/components/AccountRepair/copy';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ListEmpty, ListError, ListSkeleton } from '@/_shared/components/list/ListStates';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/_shared/components/ui/breadcrumb';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/_shared/components/ui/resizable';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRunOnceWhen } from '@/_shared/hooks/useRunOnceWhen';
import { useSiblingActionsOff } from '@/_shared/hooks/useSiblingStatus';
import { cn } from '@/_shared/lib/utils';
import { LazyAudienceActivationSheet } from '@/audiences/components/LazyAudienceActivationSheet';
import { AUDIENCE_STATE_UNREADABLE, useAudienceNeedsRepairBySource } from '@/audiences/hooks/useAudienceRepairBySource';
import { ACTIVATION_DESTINATION_TYPES } from '@/audiences/lib/activationDestinationTypes';

import { ActivationStrip } from '../components/ActivationStrip';
import { AudienceSizeCaption } from '../components/AudienceSizeCaption';
import { DeleteActivationDialog } from '../components/DeleteActivationDialog';
import { RunsPanel } from '../components/RunsPanel';
import { RunSyncButton } from '../components/RunSyncButton';
import { useDeleteActivation } from '../hooks/useDeleteActivation';
import { useRunSync } from '../hooks/useRunSync';
import { useSyncDetail } from '../hooks/useSyncDetail';
import { audiencePath, isMintedDestinationName } from '../lib/activation-list';
import { failuresRetentionDays, retentionDays } from '../lib/run-view';
import { type StackedSplitFloors, stackedSplitFloors } from '../lib/stacked-chat-split';

/**
 * The two denial strings are verbatim from rudder-webapp's `permissionChecker.ts:15-19`. They are
 * mutually exclusive here, unlike the webapp's, which lists both at once: telling someone their
 * disabled sync is "setting up" is two facts where only one is true.
 *
 * `demo` is checked before the sync's own state: in a demo workspace no run is possible for any
 * sync, so the sync-specific reasons below would send the user to fix something that would not
 * help.
 */
function denialFor(known: boolean, enabled: boolean, demo: boolean): string {
  if (demo) return 'Runs are disabled in the demo workspace — nothing is delivered to a real destination';
  // Until the sync's own row has arrived we do not know whether it is enabled, and asserting
  // "Enable sync to start run" about a sync that may already be enabled is simply untrue.
  if (!known) return 'Loading this activation…';
  if (!enabled) return 'Enable sync to start run';
  return '';
}

/**
 * The page's content column — the width of the BODY only. The header deliberately does NOT share it
 * (owner request, 2026-08-31); see the note on the `<header>` for the tradeoff that buys.
 *
 * 71rem is the panel's own widest layout, not a scale step: Band B's 744px run table (46.5rem) + `gap-6`
 * + the `w-[23rem]` run detail = 71rem. The table is fluid below that (`RunsPanel`'s `RUNS_GRID`), so the
 * cap is what stops it growing past Band B on a wide screen. Capping the container at the real content
 * width is what centres the CONTENT; a wider cap (`max-w-7xl`, 80rem — what the
 * activations LIST uses) would centre an 80rem box and leave 71rem of content pinned to its left edge,
 * i.e. this page's bug wearing a container. Centring the row with `justify-center` instead was
 * rejected for the same reason `RunsPanel` reserves the detail column: the table's left edge must not
 * move depending on whether a run is open.
 *
 * If the detail width or the gap in `RunsPanel` changes, this changes in the same edit.
 */
const CONTENT_COLUMN = 'mx-auto w-full max-w-[71rem]';

/**
 * The split's sizes, per orientation. Side by side, the audience page's split: the chat takes 38%, and
 * the runs keep at least 38% so the table stays readable. Each orientation persists under its own key,
 * so dragging on a phone never rewrites the desktop split.
 *
 * Each orientation also has its own PANEL IDS. react-resizable-panels remembers the last layout per
 * set of panel ids, and that memory wins over a new orientation's defaults and saved key, so with one
 * set a flip to a phone kept the desktop's 38% of the HEIGHT for the chat. With a set per orientation, a
 * flip lays the group out from that orientation's saved layout or defaults, open or opening, and
 * flipping back finds the other set's last layout. The ids change as props, not as React keys, so no
 * panel remounts and the chat keeps its draft. Do NOT key the group by orientation: that remounts
 * `RunsPanel`, which owns the open chat.
 *
 * Stacked (a phone), the floors are HEIGHTS that follow the split's own height (`stackedSplitFloors`,
 * which has the measurements): 15rem for the runs and 29rem for the chat while both fit, and a chat
 * floor that gives way when they do not, so the handle can always move. The default, 34/66, gives a
 * 390×844 phone ~247px of runs and a ~279px conversation.
 */
const CHAT_SPLIT = {
  horizontal: {
    autoSaveId: 'activation-chat-split-layout',
    runsId: 'activation-runs',
    chatId: 'activation-chat',
    runs: '62%',
    runsMin: '38%',
    chat: '38%',
    chatMin: '28%',
  },
  vertical: {
    autoSaveId: 'activation-chat-split-layout-stacked',
    runsId: 'activation-runs-stacked',
    chatId: 'activation-chat-stacked',
    runs: '34%',
    chat: '66%',
  },
} as const;

/** The rem floors, for a split that is not measured yet or not stacked. */
const UNMEASURED_FLOORS = stackedSplitFloors(0);

/**
 * The stacked split's floors, fitted to its measured height. The page re-renders only when a floor
 * changes, not on every pixel of a resize (a tall phone's floors never do).
 *
 * The floors are measured only while stacked. A flip back to a phone first renders the last stacked
 * floors; the observer's first callback then measures again and, when the floors differ, replaces them
 * one render later. The hook this replaced had the same one-render lag for the height, and this one adds
 * the root font size to it (a root font changed while side by side is read on that callback, not on the
 * render).
 */
function useStackedSplitFloors(el: HTMLDivElement | null): StackedSplitFloors {
  const [floors, setFloors] = useState(UNMEASURED_FLOORS);
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // The root font size is what a `rem` floor resolves against.
      const remPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const next = stackedSplitFloors(Math.round(entry.contentRect.height), remPx);
      setFloors((prev) => (prev.runsMin === next.runsMin && prev.chatMin === next.chatMin ? prev : next));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return el ? floors : UNMEASURED_FLOORS;
}

export function ActivationDetail(): React.ReactElement {
  const { sourceId = '', syncId = '' } = useParams<{ sourceId: string; syncId: string }>();
  const { wp, isActivationFailureChatEnabled } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const detail = useSyncDetail(sourceId, syncId);
  const isMobile = useIsMobile();
  const split = CHAT_SPLIT[isMobile ? 'vertical' : 'horizontal'];
  // Stacked, the floors follow the split's own height (see `CHAT_SPLIT`), so it is measured there.
  const [splitEl, setSplitEl] = useState<HTMLDivElement | null>(null);
  const stackedFloors = useStackedSplitFloors(isMobile ? splitEl : null);
  const floors = isMobile ? stackedFloors : CHAT_SPLIT.horizontal;
  // The failure chat's pane: `RunsPanel` says when the chat is open and renders it into `chatPane`.
  const [chatShown, setChatShown] = useState(false);
  const [chatPane, setChatPane] = useState<HTMLDivElement | null>(null);
  const runSync = useRunSync(syncId);
  // The three-bit rule, not `sync.enabled` — a sync disabled at its SOURCE used to head this page as
  // active. `useSyncDetail` owns the conjunction so the page and the list cannot disagree.
  //
  // Gated on the sync having ARRIVED. `isActive` treats an unresolved bit as on, which is right for a
  // dot that must not invent a disabled state, and wrong for a control that would then be armed before
  // we know what it acts on. `denialFor` already distinguishes the two cases; this is what feeds it.
  const enabled = detail.sync !== undefined && detail.isActive;

  const rawDestinationName = detail.destination?.name;

  // Ids are what this screen is addressed by; names resolve at render, so a broken reference looks
  // broken instead of rendering a stale string.
  //
  // While the lookup is still in flight neither name is known YET, which is not the same as the
  // lookup having failed — so the fallback is the id (true, and what the URL says) rather than
  // "Unknown account", which asserts a failure that has not happened. Observed in a browser: every
  // detail page opened with a confident "Unknown account" heading for ~1s before correcting itself,
  // and a genuinely missing destination looked identical to a slow one.
  const audienceLabel = detail.audienceName ?? sourceId;
  // The way BACK to the audience — the near half of the strip becomes a link, and only here.
  //
  // The id is the one thing on this page that cannot be derived from the route: `:sourceId` is the
  // rETL source, and `/audiences/:id` wants the Lookout audience. `listActivationMeta` is the only
  // read that answers it, which is why the link appears a beat after the heading does, and why it can
  // appear on its own once the repair mutation adopts a sync that had no row.
  //
  // Absent stays absent — see `lookoutAudienceId`. A sync over a source no Lookout audience owns has
  // no audience page to go to, so the label simply stays plain text. Rule 10's "disable, don't hide"
  // is about a control the reader could otherwise expect to work; there is no affordance being taken
  // away here, and a disabled-looking page title would assert a broken link where there is none.
  //
  // It also needs a NAME, not just an id, and that is a second read: the href comes from the meta row
  // while `audienceLabel` above falls back to the raw `sourceId` when `listSqlAudiences` has not
  // answered — or when the rETL source has been deleted in RudderStack and never will. Linking on the
  // id alone renders a control whose visible text is one object's id and whose target is a different
  // object, which is worse than no link: the reader cannot tell where it goes, and the one string on
  // screen actively misdescribes it. A link is labelled by its destination or it is not a link.
  const audienceHref =
    detail.lookoutAudienceId === null || detail.audienceName === undefined
      ? undefined
      : wp(audiencePath(detail.lookoutAudienceId));
  // The destination's name, but only where a person chose it. On everything Lookout mints it is the
  // `{accountName}-{uuid}` handle from `mintActivationDestinationName` — see
  // `isMintedDestinationName`. That handle used to render as a muted chip beside the title, which put
  // 36 characters of entropy on the line a reader uses to confirm they opened the right sync.
  const destinationName =
    rawDestinationName !== undefined && !isMintedDestinationName(rawDestinationName) ? rawDestinationName : undefined;
  const fallbackLabel = destinationName ?? (detail.isLoading ? syncId : 'Unknown destination');
  // `sync.config.destination.syncMode` — read again, and on purpose. This note used to say it was
  // "deliberately NOT read here any more", because it had been threaded to the run card's FOOTER as
  // "Sync mode: Mirror" where it sat beside `Type:` — a RUN property — with nothing distinguishing the
  // two scopes. That objection was about the missing scope, and Figma `334:514` supplies one: the value
  // now lands in `ConnectionSettingsBand`, under a caps label reading `CONNECTION · APPLIES TO EVERY
  // RUN`. It is still a property of the SYNC and nothing below may treat it as a run property; the band
  // is the only thing licensed to render it. See `RunOverview`'s header for the full decision.
  const syncMode = detail.sync?.config?.destination?.syncMode;
  // The sync's VDM, read off the SAME projected object as `syncMode` and passed through on the same
  // path. Read-only here: the band prints the wire values and the edit sheet is what changes them.
  // `undefined` until `detail.sync` lands — `listSyncsForSource` always emits the arrays, so an empty
  // one is a real answer and cannot be confused with a payload still in flight.
  const identifierMappings = detail.sync?.config?.destination?.identifierMappings;
  const fieldMappings = detail.sync?.config?.destination?.fieldMappings;
  const definitionName = detail.destination?.definitionName;
  // The second table's name comes from the destination's spec (`Traits` for Gainsight PX, `Attributes`
  // for LinkedIn), so the band does not name destinations itself.
  const fieldMappingsLabel = ACTIVATION_DESTINATION_TYPES.find((type) => type.name === definitionName)?.extraMappings
    ?.detailLabel;

  // The same three-rung ladder the list climbs, over the same fields — name, far-side id, destination.
  const destinationLabel = detail.destinationAudienceName ?? detail.destinationAudienceId ?? fallbackLabel;
  const hasDestinationName = detail.destinationAudienceName !== null;

  // The FAR SIDE ALONE — deliberately not the heading's string, which is the whole relation. A browser
  // tab wants a short discriminator to tell two pinned tabs apart; pointed at the title it would read
  // "Audience X activates into Destination audience Y" and truncate to the half both tabs share. Do not
  // "align" the two. It also stops short of the heading's loading placeholder: `fallbackLabel` degrades
  // to the raw `syncId` while the lookup is in flight, and a tab reading `sync-1` is worse than one that
  // simply has not been named yet.
  usePageTitle(detail.destinationAudienceName ?? detail.destinationAudienceId ?? destinationName);

  // ─── Repair path ──────────────────────────────────────────────────────────
  //
  // The name is a snapshot of something we do not own, so it goes stale, and for a sync built in the
  // RudderStack webapp — or minted before `audience_activations` existed — it was never taken at all.
  // One trigger, one mutation: a single silent attempt when the page opens on a nameless sync, which is
  // what makes this self-healing rather than a migration script. The reader sees the header settle;
  // there is no control to press. (An explicit Refresh control is deferred to a later change — when it
  // lands it becomes a second trigger for this same mutation, not a second mutation.)
  const refresh = useMutation(
    orpc.activations.refreshDestinationAudienceName.mutationOptions({
      onSuccess: () => {
        // Rule 11 — the write invalidates what it changed. That is what swaps the id in the heading for
        // a name without a reload, and it also corrects the row on the list behind this page.
        void queryClient.invalidateQueries({
          queryKey: orpc.activations.listActivationMeta.queryOptions({ input: {} }).queryKey,
        });
        // This mutation can adopt an untracked sync (insert an audience_activations row). Drop
        // audience.list so the list count chip does not stay one low until staleTime expires.
        void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
      },
    }),
  );

  // Fires at most once, and only after the meta read has ANSWERED — see `metaSettled`, which excludes an
  // errored read for the same reason it excludes a loading one: neither `null` is evidence that no name
  // is recorded, and this write reaches a third party and then persists what it finds. A sync that
  // already has a name never triggers it at all.
  useRunOnceWhen(detail.metaSettled && Boolean(syncId) && detail.destinationAudienceName === null, () => {
    refresh.mutate({ sourceId, syncId });
  });

  // ─── Edit and delete ──────────────────────────────────────────────────────
  //
  // Both live in a ⋯ menu, the same pair in the same order the list row offers (`ActivationsTable`):
  // Edit above Delete, so the reversible action comes first and the destructive one sits at the bottom
  // edge. A bare Delete button here was the odd one out — it made the two surfaces disagree about what
  // an activation can have done to it, and it put the only destructive control on the page at the same
  // altitude as the run control.
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The edit sheet is mounted only while it is open, exactly as the list mounts it: `readActivationConfig`
  // is keyed off `open`, so an always-mounted sheet would fetch this sync's settings on every render of
  // a page whose job is showing runs.
  const [editing, setEditing] = useState(false);

  // The same hook the list's ⋯ menu uses — one definition of what `deleted: false` means. All this
  // page adds is where to go afterwards: it is addressing a connection that no longer exists.
  const { remove, isPending: removing } = useDeleteActivation(() => void navigate(wp('/activations')));

  // The demo-workspace gate — read the way `useChallengeCreators` does, straight off `workspace.get`.
  // A demo workspace performs zero RudderStack writes, so neither Run sync nor Edit may reach the
  // server here: both would either lie (an action that looks like it did something) or bounce off the
  // service layer's own refusal (`runActivationSync`'s short-circuit, `readActivationConfig`'s CONFLICT).
  //
  // `workspace.get` is not warmed by the app shell, so on a cold load of this page `demoKnown` can be
  // false after `detail.sync` has already arrived. Folding it into `known` — the same bit `denialFor`
  // already uses to keep the button off before the sync itself is known — closes that window instead
  // of opening a second one: `demo` defaulting to `false` while unknown must never make `canStart`
  // true. Fail closed, not open.
  const { data: workspaceData } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const demo = workspaceData?.challengeMode ?? false;
  // KNOWN means the answer arrived, not that the query stopped being `pending`. TanStack's `isPending`
  // is phase only, so an ERRORED `workspace.get` reads as "finished, answer no" — which put both Edit
  // surfaces back in play in a demo workspace and took the callout down with them. Fail closed covers
  // the failure, not just the wait.
  const known = detail.sync !== undefined && workspaceData !== undefined;
  // ACT2-1124: a disabled or gone sibling turns activation actions off, with the banner's text as the reason.
  const siblingActions = useSiblingActionsOff();
  // The activation failure chat (spec decision 8 and the gating table). The server answers NOT_FOUND
  // in every hidden case; this mirrors it so no dead control renders. Fail closed: an unknown demo bit
  // (`workspaceData` not in yet) shows nothing yet. Sibling workspaces (ACT2-1124) have it too: the
  // server reads failed records there through rudder-mcp's `/lookout-mcp`, as the person asking.
  const chatEnabled =
    isActivationFailureChatEnabled && workspaceData !== undefined && !demo && detail.lookoutAudienceId !== null;
  // The drawer's activation chip: the destination (its chosen name, else its platform) and the far-side
  // audience, as far as the page knows them.
  const chatActivationLabel =
    [destinationName ?? detail.destination?.displayName, detail.destinationAudienceName ?? detail.destinationAudienceId]
      .filter((part): part is string => Boolean(part))
      .join(' · ') || null;
  const { data: chatAvailability, isError: chatAvailabilityFailed } = useQuery({
    ...orpc.activationChat.availability.queryOptions({ input: { syncId } }),
    enabled: chatEnabled,
  });
  const canStart = known && enabled && !demo && !siblingActions.off;
  const denialReason = siblingActions.off ? siblingActions.reason : denialFor(known, enabled, demo);
  /** Why `Edit` is unavailable, for BOTH surfaces offering it: the ⋮ menu and the connection band. */
  // ACT2-1124 LLD §5.9: an off sibling disables Edit and Delete too, with the banner's text, before any other reason.
  // ACT2-1286: Edit waits for the audience's repair. Run sync, Stop sync and Delete stay allowed: they act
  // on RudderStack's own rETL account, not on Lookout's warehouse account.
  const audienceRepair = useAudienceNeedsRepairBySource()(sourceId);
  const editDenialReason = siblingActions.off
    ? siblingActions.reason
    : !known
      ? 'Loading this activation…'
      : demo
        ? 'Editing is not available in the demo workspace — delete this activation and create a new one'
        : audienceRepair === 'needs_repair'
          ? REPAIR_AUDIENCE_FIRST
          : audienceRepair === 'failed'
            ? AUDIENCE_STATE_UNREADABLE
            : audienceRepair === 'unknown'
              ? 'Loading this activation…'
              : undefined;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <header className="border-b px-6 py-4">
        {/* The header runs FULL-BLEED while the body stays in `CONTENT_COLUMN`. This deliberately
            reverses the earlier "one shared width" rule (owner request, 2026-08-31): the title band is
            meant to span the viewport the way every other detail page's header does, so the page reads
            as one rule over one full band rather than a centred row under a full-width line.

            The cost is real and was measured before the reversal: at 2000px the run control sits well
            right of the panel it acts on, and the title no longer starts where the table does. That is
            accepted here, not overlooked — this header carries the page's primary ACTION, which is
            exactly why it was once kept in the column. If the misalignment is ever judged the worse
            evil, re-wrap this div in `CONTENT_COLUMN`; do NOT widen the body to meet it, which is
            pinned to `RunsPanel`'s real width. */}
        <div className="w-full">
          {/* `Breadcrumb`, matching every other detail page in the app. This page used to hand-roll a
            `←` back link instead, on the argument that a trail of one is a back link wearing a
            landmark. The argument has some force, but it was applied to the wrong side: seven surfaces
            use the breadcrumb and this was the only one that did not, so the consistency case beats it.
            Raised in review on #660 — if the one-crumb trail is ever reconsidered, it moves for all of
            them at once, not here alone.

            NO trailing `BreadcrumbSeparator` — see `AudiencePage` for why a chevron pointing at
            nothing is the one part of the old markup worth losing.

            The AUDIENCE is the second crumb, and it is a `BreadcrumbPage`, not a `BreadcrumbLink`.
            This note used to say the audience crumb was "deliberately absent: there is still no
            per-audience activations route for it to point at" — that reason is intact and it only ever
            ruled out a LINK. Figma `334:514` (`334:515`) draws the trail as
            `Activations › Active Trialists Conversion Candidates`, and the last crumb of a trail is the
            page you are already on: it takes no href, so the missing route does not block it. If that
            route ever lands, this becomes a `BreadcrumbLink` and nothing else changes.

            Rendered only once `detail.audienceName` has actually arrived — `audienceLabel` falls back
            to the raw `sourceId`, and a 27-character id is not a crumb. While the lookup is in flight
            the trail is one crumb, exactly as it was. */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={wp('/activations')}>Activations</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {detail.audienceName === undefined ? null : (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{detail.audienceName}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>

          {/* The title row. Every sibling is `shrink-0` and the heading is `min-w-0 flex-1`, so the strip
            alone absorbs the shortfall — and anything added here later (a status control, a menu) drops
            in as one more `shrink-0` child without re-deriving the strip's truncation. */}
          <div className="flex items-center gap-4">
            {/* The strip IS the heading, rather than sitting under one that restates half of it.
              The pair is the identity of an activation — that is the strip's whole premise — and the
              far side alone was the old h1, with the strip repeating it one line below.

              The wrapper carries NO visual type: `LABEL_CLASSES.lg` already sets the page-title recipe
              on the labels, so the h1 exists for the ROLE. It also carries no `aria-label`, and that is
              load-bearing rather than an omission — see below.

              ── Do not put an `aria-label` (or `aria-labelledby`) on this h1. ──
              `heading` is a name-from-content role, so with no label the accessible name is computed by
              walking the subtree: the marks, the arrow and the pilot light are all `aria-hidden` and
              drop out, while the strip's `sr-only` role words and connective stay in. The name is
              therefore the whole sentence — "Audience X activates into Destination audience Y", or
              "… is not currently syncing into …" when the sync is off. An `aria-label` REPLACES that,
              which would silence the sync state on the last surface that announces it: the list and
              `ActivationCard` both wrap the strip in a labelled control, so there the state has to
              travel separately through `activationRowLabel()`'s ", disabled" suffix. Here nothing
              replaces the contents, so the state must stay OUT of any label. The pilot light would look
              perfect throughout, which is why the guard is a test on the accessible NAME. */}
            <h1 className="m-0 min-w-0 flex-1 text-base font-normal tracking-normal">
              {/* The identifier styling is passed through so an unresolved far side stays typeset as an
                id at title altitude — the product does not claim to have named something `23847…`.

                `layout` stays `flow` (the default). `tracks` exists to make the arrow scannable as a
                column down a list, and there is one strip on this page, so there is no column to form.
                Worse, a track is a reservation and not a request: at 1440 this strip is ~1150px, track
                one would reserve ~680px, and a short audience name renders ~60px into it — stranding
                the arrow ~620px right of where the name ends, with nothing in between.

                `enabled` is only passed once the sync's own row has arrived: until then the state is
                not known, and an absent dot says that honestly where a green one would assert a sync is
                live before anything confirmed it. */}
              <ActivationStrip
                audienceLabel={audienceLabel}
                audienceHref={audienceHref}
                warehouseType={detail.sourceDefinitionName}
                destinationLabel={destinationLabel}
                destinationDefinitionName={definitionName}
                destinationIsIdentifier={!hasDestinationName}
                enabled={detail.sync ? enabled : undefined}
                size="lg"
              />
            </h1>
            <div className="flex shrink-0 items-center gap-2">
              <RunSyncButton
                state={runSync.state}
                canStart={canStart}
                denialReason={denialReason}
                destinationDefinitionName={definitionName}
                onStart={(syncType) => runSync.startSync(syncType)}
                onStop={runSync.stopSync}
              />
              {/* Overflow, not a second button: running the sync is this page's primary action and the
                only one that stays spelled out. Delete is destructive-styled INSIDE the menu, where a
                mis-click cannot reach it, and the confirmation is still where the consequence is
                stated.

                Named by the activation, like the list's row menus: a screen reader hearing a bare
                "Actions" cannot tell what it is about to delete, and this page's whole risk is that a
                pair of similar syncs look alike. */}
              <KebabMenu
                label={`Actions for ${destinationLabel}`}
                actions={[
                  {
                    label: 'Edit',
                    icon: Pencil,
                    disabled: editDenialReason !== undefined,
                    disabledReason: editDenialReason ?? '',
                    onSelect: () => setEditing(true),
                  },
                  {
                    label: 'Delete',
                    icon: Trash2,
                    destructive: true,
                    disabled: siblingActions.off,
                    disabledReason: siblingActions.reason,
                    onSelect: () => setConfirmOpen(true),
                  },
                ]}
              />
            </div>
          </div>
          {/* A third band under the title row. Size is a property of the AUDIENCE, so it sits under the
              near side rather than in the strip, which reads as the audience→destination relationship.
              It arrives a beat after the rest of the header (its query is sequential behind
              `listActivationMeta`), and renders nothing at all for a sync with no Lookout audience. */}
          <AudienceSizeCaption
            record={detail.audienceRecord}
            loading={detail.audienceRecordLoading}
            error={detail.audienceRecordError}
          />
        </div>
      </header>

      {/* Mounted only while open — see `editing`. `audienceLabel` is passed so the sheet names the near
          side the same way the heading does; the far side and every mapping come from the sync itself. */}
      {editing ? (
        <LazyAudienceActivationSheet
          mode="edit"
          open
          onOpenChange={(open) => !open && setEditing(false)}
          syncId={syncId}
          audienceLabel={audienceLabel}
          warehouseType={detail.sourceDefinitionName}
        />
      ) : null}

      <DeleteActivationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        destinationLabel={destinationLabel}
        loading={removing}
        onConfirm={() => remove(syncId)}
      />

      {/* Body: a resizable split, the same as the audience page's (`AudiencePage`). The runs fill it
          while the failure chat is closed; `Ask why` adds the chat as a right-hand pane, from below
          the header to the bottom of the window, with a handle to trade table width for chat width.
          Horizontal on desktop, stacked on mobile: one group that flips, so there is one chat either way.
          See `ActivationFailureChatDrawer`'s doc for why a pane and not an overlay. */}
      <ResizablePanelGroup
        direction={isMobile ? 'vertical' : 'horizontal'}
        autoSaveId={split.autoSaveId}
        elementRef={setSplitEl}
        className="min-h-0 flex-1"
      >
        <ResizablePanel id={split.runsId} defaultSize={chatShown ? split.runs : '100%'} minSize={floors.runsMin}>
          {/* The panel scrolls, but nothing in it overflows vertically: `RunsPanel`'s `fillHeight` table
              needs a DEFINITE-height ancestor, so this is a `flex-col` whose children shrink to its
              height (`min-h-0 flex-1` all the way down) and `RunsPanel` scrolls its OWN pieces (the
              table, the detail column). The overflow is only a backstop for a very short window.
              `scrollbar-gutter: stable`: the strip with the chat closed grows to its content and can
              overflow, and a classic (non-overlay) scrollbar appearing then took ~15px from the width
              `RunsPanel` measures for the 48rem rule, so near the threshold the layout depended on
              which way the window was resized. The gutter keeps that width the same either way. */}
          <div
            className={cn(
              'flex h-full min-h-0 [scrollbar-gutter:stable] flex-col overflow-y-auto px-6 py-6',
              // Stacked under the chat, the band's padding is room a run row needs (see `CHAT_SPLIT`).
              chatShown && 'max-md:py-3',
            )}
          >
            {/* The centring layer, kept SEPARATE from the box above: that one owns the padding, the
                scroll behaviour and the definite height, this one owns the width. It is
                `flex-1 flex-col min-h-0` so the height chain passes straight through it to `RunsPanel`. */}
            <div className={cn(CONTENT_COLUMN, 'flex min-h-0 flex-1 flex-col')}>
              {detail.error && !detail.sync ? (
                <ListError title="Could not load this activation" message={detail.error.message}>
                  <Button variant="secondary" onClick={detail.refetch}>
                    Retry
                  </Button>
                </ListError>
              ) : detail.isLoading ? (
                <ListSkeleton view="card" />
              ) : !detail.sync ? (
                <ListEmpty
                  icon={History}
                  title="Activation not found"
                  description="This sync is no longer part of this audience. It may have been deleted in RudderStack."
                />
              ) : (
                // `detail.sync` is settled and present from here down (every branch above already returned).
                // `!enabled` used to replace this WHOLE region with a full-panel "Sync disabled" empty state
                // — the run table, the range picker, the retention sentence and the run detail all vanished
                // along with it, which took away exactly what someone reaches for when a sync is paused: why
                // it was paused, what it last did, whether it failed. A disabled sync's run history is not
                // beside the point, so `!enabled` now renders a NOTICE above the panel rather than replacing
                // it — the panel itself renders unconditionally. Everything past that (loading, error,
                // never-run, content) stays `RunsPanel`'s own business: it already owns the run-history query
                // and already knows whether there is anything to show.
                <>
                  {/* Demo workspace: the runs below are generated, not read (ACT2-649). A visitor is a
                  prospective customer, and a page of convincing run data with nothing saying where it
                  came from is the one way this feature could mislead rather than demonstrate. Stated
                  once, above the panel, rather than repeated on every row.

                  It cannot collide with the `Sync disabled` notice below: a demo sync is served from
                  `listSyncsForSource`'s demo branch, which hardcodes `enabled: true`. */}
                  {demo ? (
                    <Callout variant="info" className="mb-4 shrink-0">
                      No sync runs in a demo workspace. These runs were generated to fill this page, and nothing was
                      delivered.
                    </Callout>
                  ) : null}
                  {!enabled ? (
                    <Callout variant="warning" className="mb-4 shrink-0">
                      <p className="font-medium">Sync disabled</p>
                      <p className="mt-0.5">Enable sync to resume runs.</p>
                    </Callout>
                  ) : null}
                  <RunsPanel
                    // Keyed, so a different sync is a different component. React Router reuses this route
                    // element when only `:syncId` changes, and `RunsPanel` holds selection refs plus a
                    // page/range in `usePastRuns` that are all scoped to ONE sync. Without the key, opening
                    // activation B from activation A inherits A's `hasDefaulted` (so B never auto-selects its
                    // newest run and lands on "No run selected"), A's page, and A's date window.
                    key={syncId}
                    syncId={syncId}
                    destinationDefinitionName={definitionName}
                    // The warehouse that did the extracting, for the open run's Extract mark. Data-driven
                    // off the sync's own source row — never the Figma example.
                    sourceDefinitionName={detail.sourceDefinitionName}
                    // The header's poll knows a run exists before this table's page can — without this a run
                    // started from the button above appeared nowhere until a manual refresh. Deliberately
                    // the WIDE fact (any run in flight, cron fires included): a run nobody on this page
                    // asked for still has to become visible in the table.
                    live={runSync.state !== 'idle'}
                    // …and the NARROW one, which is what may move the reader's selection. See both props'
                    // doc comments: discovering a run and following it are different rights.
                    userStartedRun={runSync.userStartedRun}
                    // Window-independent — see `RunsPanel`'s `hasEverRun` doc comment. `detail.sync` is always
                    // defined here (the `!detail.sync` branch above already returned), so this reads the
                    // platform's own `lastRun` directly rather than asking the windowed run-history query,
                    // which cannot tell "never run" apart from "ran, but not in this window".
                    hasEverRun={detail.sync.lastRun !== undefined}
                    // This connection's own retention, not the platform default. `detail.sync` is always
                    // defined by the time this renders — the `!detail.sync` branch above returns its own
                    // `ListEmpty` before this point — so `retentionDays()`'s own `undefined` fallback is dead
                    // code on this call site specifically (it stays load-bearing for `retentionDays`'s other,
                    // more permissive contract: `RawSync | undefined`). The real payoff of that guard:
                    // `usePastRuns`'s range `useState` initializer runs ONCE, on mount, and by then it already
                    // sees the true resolved number — there is no mount-before-sync-arrives race to paper over
                    // here.
                    retentionDays={retentionDays(detail.sync)}
                    // Sync-level, for the run detail's connection band — never for a row in the table.
                    syncMode={syncMode}
                    // Sync-level too, and read-only: `Edit` below is what changes them.
                    identifierMappings={identifierMappings}
                    fieldMappings={fieldMappings}
                    fieldMappingsLabel={fieldMappingsLabel}
                    // The band's `Edit` opens the SAME sheet the page's ⋮ menu opens, which is the surface
                    // that actually owns Log retention (`ActivationSyncSchedule`'s
                    // `activation-log-retention-days`). One editor, reached from both places, rather than a
                    // second route that would have to be kept in step with it.
                    onEditConnection={() => {
                      setEditing(true);
                    }}
                    // Disabled, not withheld — dropping `onEditConnection` would hide the mapping rows too.
                    editConnectionDisabledReason={editDenialReason}
                    chatEnabled={chatEnabled}
                    perRecordReads={chatAvailability?.perRecordReads ?? null}
                    linkModel={chatAvailability?.linkModel ?? null}
                    chatAvailabilityFailed={chatAvailabilityFailed}
                    activationLabel={chatActivationLabel}
                    failuresRetentionDays={failuresRetentionDays(detail.sync)}
                    chatPane={chatPane}
                    onChatShownChange={setChatShown}
                  />
                </>
              )}
            </div>
          </div>
        </ResizablePanel>
        {chatShown ? (
          <>
            <ResizableHandle withHandle aria-label="Resize the failure chat panel" />
            <ResizablePanel id={split.chatId} defaultSize={split.chat} minSize={floors.chatMin}>
              <div ref={setChatPane} className="flex h-full min-h-0 flex-col overflow-hidden" />
            </ResizablePanel>
          </>
        ) : null}
      </ResizablePanelGroup>
    </div>
  );
}
